import React, { useCallback, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import SetPageTitle from 'components/Portals/SetPageTitle';
import ContainerBox from 'components/ContainerBox';
import { BaseCustomersTable } from 'components/CustomersTable';
import { api } from 'common/frontend/api';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks';
import SearchBar from 'components/SearchBar';
import { getDatesFromDateRange } from 'components/CustomersTable/utils/dates';
import Container from 'components/Container';
import { getAllCustomersBySearchTerm } from './utils';
import { usePromise } from 'hooks/usePromise';
import { DateRangePicker } from 'components/DataGridDateRangePicker';
import { DataGridDateRange } from 'components/DataGridDateRangePicker/types';
import { useTranslation } from 'services/localization';

const Customers = () => {
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const { t } = useTranslation();

	const [page, setPage] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(25);
	const [searchValue, setSearchValue] = useState<string>('');
	const [dateRange, setDateRange] = useState<DataGridDateRange>({
		type: 'default',
		limit: pageSize,
	});

	const [startAt, endAt] = useMemo(() => {
		return getDatesFromDateRange(dateRange).slice().reverse();
	}, [dateRange]);

	const query = useMemo(() => {
		let customers = api().customers.get.where('merchantId', '==', shopId);
		customers = customers.orderBy('lastPurchaseDate', 'desc');

		if (!!startAt) {
			customers = customers.startAt(startAt.toISOString());
		}

		if (!!endAt) {
			customers = customers.endAt(endAt.toISOString());
		}

		return customers;
	}, [endAt, shopId, startAt]);

	const getCustomersWithSearchTerm = useCallback(() => {
		return getAllCustomersBySearchTerm({
			shopId,
			searchTerm: searchValue,
		});
	}, [searchValue, shopId]);

	const customers = useQuery({
		query,
		initialLimit: dateRange.type === 'all' ? 10000 : pageSize * 2,
	});

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		const newLimit = (newPage + 2) * pageSize;

		customers.setLimit((prev) => {
			if (newLimit > prev) return newLimit;
			return prev;
		});
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		const newLimit = (page + 2) * newPageSize;

		customers.setLimit((prev) => {
			if (newLimit > prev) return newLimit;
			return prev;
		});
	};

	const searchResults = usePromise({ fetcher: getCustomersWithSearchTerm });

	const maxPage = Math.max(Math.ceil(customers.count / pageSize) - 1, 0);

	const data = !!searchValue ? searchResults : customers;

	return (
		<>
			<SetPageTitle
				styling={{ fluid: true }}
				priority={1}
				title={t('customers.pageTitle', 'Customers')}
			/>
			<Container fluid>
				<Stack direction="row" spacing={2}>
					<Box flex={1}>
						<SearchBar
							onChange={setSearchValue}
							placeholder={t(
								'customers.searchBarPlaceholder',
								'Search by customer name, email or phone number',
							)}
						/>
					</Box>
					<DateRangePicker
						value={!!searchValue ? { type: 'all' } : dateRange}
						onChange={(value) => {
							setDateRange(value);
							setPage(0);
							customers.setLimit(value.type === 'all' ? 10000 : pageSize * 2);
						}}
						disabled={!!searchValue}
						defaultItem={{
							label: t('common:times.latest'),
							limit: pageSize,
						}}
						visibleOptionsList={['default', 'custom', 'all']}
					/>
				</Stack>
				<ContainerBox padding="none" data-sl="mask">
					<BaseCustomersTable
						customers={data.data ?? []}
						searchValue={searchValue}
						dateRange={dateRange.type}
						loading={data.loading}
						page={page > maxPage ? maxPage : page}
						onPageChange={handlePageChange}
						pageSize={pageSize}
						onPageSizeChange={handlePageSizeChange}
						limit={dateRange.type === 'all' ? 10000 : customers.limit}
					/>
				</ContainerBox>
			</Container>
		</>
	);
};

export default Customers;
